import { css, html, LitElement, TemplateResult } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { avatarStyle, COLOR_PAIRS, profileInitial } from "../shared/colors.js";
import { subjectSummary } from "../shared/teacher.js";
import { sampleTextAtLevel } from "../shared/corpus.js";
import { questionForLevel } from "../shared/math.js";
import { SPELLING_CURRICULUM } from "../shared/spelling-curriculum.js";
import { pictureFor } from "../shared/letter-pictures.js";
import { shareAppLink } from "../shared/share.js";
import { createBackup } from "./backup.js";
import { APP_RELEASED_AT, APP_SEMVER, formatAppTimestamp, rememberDeviceVersion } from "../shared/app-version.js";
import { MAX_LEVEL, MIN_LEVEL, Profile, Subject } from "../shared/type.app.js";
import { ReadingBeeModal } from "./component.modal.js";
import { ReadingBeePasscode } from "./component.passcode.js";
import { StoreController } from "./controller.store.js";
import { SuccessEvent } from "./event.success.js";
import { WarningEvent } from "./event.warning.js";
import {
  authSession,
  getAuthUser,
  isAuthConfigured,
  loginInstructor,
  logoutInstructor,
  type AuthUser,
} from "./auth0.js";
import {
  clearSqlLocalData,
  flushSqlSync,
  getSqlOutbox,
  getSqlSyncStatus,
  removeSqlProfileData,
  sqlSyncSession,
  type SqlSyncStatus,
} from "./sql-sync.js";
import { readingBeeApi } from "./sql-api.js";
import {
  backIcon,
  downloadIcon,
  instructionsIcon,
  lockIcon,
  shareIcon,
  trashIcon,
  unlockIcon,
  uploadIcon,
  versionIcon,
} from "./icons.js";
import { navigate } from "./nav.js";
import { appStore } from "./store.js";
import { globalStyles } from "./styles.global.js";
import { dispatch } from "./util.events.js";
import "./component.instructor-gate.js";
import "./component.modal.js";
import "./component.passcode.js";

@customElement("reading-bee-settings")
export class ReadingBeeSettings extends LitElement {
  static override styles = [
    globalStyles,
    css`
      :host {
        display: block;
        min-height: 100%;
        max-width: 100%;
        overflow-x: clip;
      }

      .page {
        min-height: 100dvh;
        min-width: 0;
        max-width: 100%;
        display: grid;
        grid-template-rows: auto 1fr;
      }

      header {
        position: sticky;
        top: 0;
        z-index: 2;
        display: grid;
        grid-template-columns: 44px 1fr 44px;
        align-items: center;
        padding: calc(0.7rem + var(--inset-top)) 1rem 0.8rem;
        background: linear-gradient(to bottom, #0c0b09 70%, rgba(12, 11, 9, 0.86));
        border-bottom: 1px solid var(--color-panel-border);
      }

      header h1 {
        margin: 0;
        font-size: 1.2rem;
        text-align: center;
      }

      .back {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        color: var(--color-primary-text);
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
      }

      .body {
        width: min(640px, 100%);
        min-width: 0;
        max-width: 100%;
        margin: 0 auto;
        padding: 1.2rem 1.2rem calc(6.5rem + env(safe-area-inset-bottom));
      }

      h2 {
        margin-top: 2.25rem;
      }

      .account-heading {
        margin-top: 0;
      }

      h2[slot="title"] {
        margin: 0;
      }

      .top-actions {
        margin-top: 1.6rem;
        margin-bottom: 0.4rem;
      }

      .top-actions .ghost-btn,
      .data-actions .ghost-btn {
        flex-direction: column;
        gap: 0.22rem;
        font-size: 0.78rem;
        font-weight: 700;
        line-height: 1.15;
        text-align: center;
        white-space: normal;
        min-height: 3.7rem;
        padding: 0.45rem 0.35rem;
      }

      .version-facts {
        display: grid;
        gap: 0.85rem;
        margin: 0;
      }

      .version-facts div {
        display: grid;
        gap: 0.18rem;
      }

      .version-facts dt {
        margin: 0;
        color: var(--color-primary-text-muted);
        font-size: 0.82rem;
        font-weight: 700;
      }

      .version-facts dd {
        margin: 0;
        font-size: 1.05rem;
        font-weight: 700;
      }

      .lock-explain {
        display: grid;
        gap: 1rem;
      }

      .lock-explain p {
        margin: 0;
      }

      .lock-explain .primary-btn {
        width: 100%;
      }

      .lock-pad {
        display: grid;
        justify-items: center;
      }

      .account {
        display: grid;
        gap: 0.55rem;
        padding: 1rem 1.05rem;
        border-radius: 18px;
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
      }

      .account p {
        margin: 0;
      }

      .account b {
        color: var(--color-primary-text);
      }

      .account-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.8rem;
      }

      .account-row p {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .account-row .sign-out {
        flex: 0 0 auto;
      }

      .account .actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.55rem;
      }

      .account .sync-error {
        color: var(--color-danger, #e85d4c);
      }

      @media (max-width: 640px) {
        .account-row {
          flex-direction: column;
          align-items: stretch;
        }

        .account-row p {
          white-space: normal;
        }

        .account-row .sign-out {
          width: 100%;
        }
      }

      .stack {
        display: grid;
        gap: 0.8rem;
        min-width: 0;
      }

      .profile-card {
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
        border-radius: 18px;
        padding: 0.9rem;
        display: grid;
        gap: 0.7rem;
        min-width: 0;
        cursor: pointer;
        text-align: left;
        width: 100%;
        transition:
          border-color var(--time-normal) ease,
          transform var(--time-normal) ease,
          background var(--time-normal) ease;
      }

      .profile-card:hover {
        border-color: var(--color-1);
        background: #221e18;
        transform: translateY(-1px);
      }

      .profile-card:focus-visible {
        outline: none;
        border-color: var(--color-1);
        box-shadow: 0 0 0 3px rgba(232, 184, 74, 0.18);
      }

      .profile-top {
        display: flex;
        align-items: center;
        gap: 0.7rem;
        min-width: 0;
      }

      .swatch {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        flex: 0 0 auto;
        display: grid;
        place-items: center;
        font-weight: 700;
        font-size: 0.9rem;
        line-height: 1;
        border: 0;
        box-shadow: 0 0 0 3px transparent;
        cursor: pointer;
        overflow: hidden;
        transition:
          box-shadow var(--time-normal) ease,
          transform var(--time-normal) ease;
      }

      .swatch:hover {
        transform: scale(1.08);
        box-shadow: 0 0 0 3px var(--color-1);
      }

      .identity {
        flex: 1;
        min-width: 0;
        display: grid;
        gap: 0.12rem;
        text-align: left;
        background: none;
        border: 0;
        padding: 0.1rem 0;
        color: inherit;
        font: inherit;
        cursor: pointer;
        border-radius: 12px;
      }

      .identity:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px rgba(232, 184, 74, 0.28);
      }

      .identity strong {
        font-size: 1.02rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .identity small {
        color: var(--color-primary-text-muted);
        font-size: 0.78rem;
        line-height: 1.25;
      }

      .level {
        color: var(--color-1);
        font-size: 0.88rem;
        font-weight: 700;
        padding: 0.35rem 0.7rem;
        min-height: 36px;
        border-radius: 999px;
        border: 1px solid rgba(232, 184, 74, 0.35);
        background: rgba(232, 184, 74, 0.1);
        flex: 0 0 auto;
        white-space: nowrap;
        transition:
          background var(--time-normal) ease,
          border-color var(--time-normal) ease;
      }

      .level:hover {
        background: rgba(232, 184, 74, 0.2);
        border-color: var(--color-1);
      }

      .level-form {
        display: grid;
        gap: 1rem;
      }

      .level-subjects {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        overflow: hidden;
        border-radius: 999px;
      }

      .level-subjects button {
        min-height: 44px;
        border-radius: 0;
      }

      .level-subjects button:first-child {
        border-radius: 999px 0 0 999px;
      }

      .level-subjects button:last-child {
        border-radius: 0 999px 999px 0;
      }

      .level-subjects button + button {
        border-left: 1px solid var(--color-panel-border);
      }

      .level-controls {
        display: grid;
        grid-template-columns: 1fr 5.5rem;
        gap: 0.7rem;
        align-items: center;
      }

      input[type="range"] {
        appearance: none;
        height: 8px;
        padding: 0;
        border-radius: 999px;
        background: #221e18;
        border: 1px solid var(--color-panel-border);
      }

      input[type="range"]::-webkit-slider-thumb {
        appearance: none;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: var(--color-1);
        box-shadow: 0 0 0 3px rgba(232, 184, 74, 0.2);
        cursor: pointer;
      }

      input[type="range"]::-moz-range-thumb {
        width: 22px;
        height: 22px;
        border: 0;
        border-radius: 50%;
        background: var(--color-1);
        cursor: pointer;
      }

      input[type="number"] {
        text-align: center;
        font-weight: 700;
        padding: 0.7rem 0.4rem;
      }

      .sample {
        min-height: 5.5rem;
        border-radius: 18px;
        background: #120f0c;
        border: 1px solid var(--color-panel-border);
        display: grid;
        place-items: center;
        gap: 0.7rem;
        padding: 1.1rem 1rem;
        text-align: center;
        font-family: var(--font-reading);
        font-weight: 500;
        line-height: 1.35;
      }

      .sample.has-picture {
        min-height: 9rem;
      }

      .sample .picture {
        width: 88px;
        height: 88px;
        object-fit: contain;
        filter: drop-shadow(0 8px 14px rgba(0, 0, 0, 0.35));
      }

      .sample[data-kind="letter"] {
        font-size: 2.6rem;
      }

      .sample[data-kind="word"] {
        font-size: 2rem;
      }

      .sample[data-kind="phrase"] {
        font-size: 1.45rem;
      }

      .sample[data-kind="sentence"],
      .sample[data-kind="book"] {
        font-size: 1.15rem;
      }

      .sample.is-long {
        font-size: 0.92rem;
        line-height: 1.28;
        text-align: left;
        max-height: 14rem;
        overflow-x: hidden;
        overflow-y: auto;
        white-space: pre-wrap;
        place-items: start;
        justify-items: start;
        scrollbar-width: thin;
      }

      .sample-label {
        margin: 0;
        font-size: 0.88rem;
        color: var(--color-primary-text-muted);
      }

      .pairs {
        display: grid;
        grid-template-columns: repeat(6, minmax(0, 1fr));
        gap: 0.5rem;
        padding: 0.2rem 0 0.6rem;
      }

      .pair {
        width: 100%;
        min-width: 0;
        aspect-ratio: 1;
        height: auto;
        border-radius: 50%;
        box-shadow: 0 0 0 3px transparent;
        transition:
          box-shadow var(--time-normal) ease,
          transform var(--time-normal) ease;
      }

      .pair:hover {
        transform: scale(1.08);
        box-shadow: 0 0 0 3px var(--color-1);
      }

      .pair[selected] {
        box-shadow: 0 0 0 3px #fff;
      }

      .pair[selected]:hover {
        box-shadow:
          0 0 0 3px #fff,
          0 0 0 6px rgba(232, 184, 74, 0.45);
      }

      .picker-title {
        margin: 0;
      }

      .danger {
        margin-top: 2.5rem;
        padding: 1rem;
        border-radius: 18px;
        border: 1px solid rgba(232, 93, 76, 0.35);
        background: rgba(232, 93, 76, 0.06);
      }

      .danger h2 {
        margin-top: 0;
      }

      .danger .danger-btn {
        width: 100%;
      }

      .confirm {
        display: grid;
        gap: 0.8rem;
      }

      .confirm-row {
        display: flex;
        gap: 0.6rem;
        flex-wrap: wrap;
      }

      .icon-delete,
      .icon-share {
        min-width: 40px;
        min-height: 40px;
        padding: 0.35rem;
        flex: 0 0 auto;
      }

      .icon-share:hover {
        color: var(--color-1);
        background: rgba(232, 184, 74, 0.14);
      }

      .icon-delete:hover {
        color: var(--color-danger);
        background: rgba(232, 93, 76, 0.14);
      }

      .skeleton {
        width: 100%;
        min-height: 58px;
        border-radius: 18px;
        border: 2px dashed rgba(244, 234, 213, 0.28);
        background: transparent;
        display: flex;
        align-items: center;
        gap: 0.7rem;
        padding: 0.9rem;
        color: var(--color-primary-text-muted);
        font-weight: 700;
        transition:
          border-color var(--time-normal) ease,
          color var(--time-normal) ease;
      }

      .skeleton:hover {
        border-color: var(--color-1);
        color: var(--color-primary-text);
      }

      .ghost-swatch {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 2px dashed rgba(244, 234, 213, 0.28);
        flex: 0 0 auto;
        transition: border-color var(--time-normal) ease;
      }

      .skeleton:hover .ghost-swatch {
        border-color: var(--color-1);
      }
    `,
  ];

  @state() private updatingPasscode = false;
  @state() private passcodeStep: "current" | "next" | "confirm" = "current";
  @state() private nextPasscode = "";
  @state() private wipeStep: "none" | "confirm" | "pin" = "none";
  @state() private pendingDeleteId: string | null = null;
  @state() private pendingDeleteName = "";
  @state() private colorPickerProfileId: string | null = null;

  @state() private levelProfileId: string | null = null;
  @state() private draftLevel = 1;
  @state() private levelSubject: Subject = "reading";
  @state() private authUser: AuthUser | null = null;
  @state() private authBusy = false;
  @state() private syncStatus: SqlSyncStatus = getSqlSyncStatus();
  @query(".color-modal") private colorModal!: ReadingBeeModal;
  @query(".delete-modal") private deleteModal!: ReadingBeeModal;
  @query(".level-modal") private levelModal!: ReadingBeeModal;
  @query(".lock-modal") private lockModal!: ReadingBeeModal;
  @query(".version-modal") private versionModal!: ReadingBeeModal;
  @state() private lockStep: "explain" | "create" | "confirm" | "disable" = "explain";
  @state() private pendingLockPasscode = "";

  constructor() {
    super();
    new StoreController(this);
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.authUser = getAuthUser();
    this.syncStatus = getSqlSyncStatus();
    authSession.addEventListener("change", this.onAuthChange);
    sqlSyncSession.addEventListener("change", this.onSyncChange);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    authSession.removeEventListener("change", this.onAuthChange);
    sqlSyncSession.removeEventListener("change", this.onSyncChange);
  }

  private onAuthChange = (): void => {
    this.authUser = getAuthUser();
  };

  private onSyncChange = (): void => {
    this.syncStatus = getSqlSyncStatus();
  };

  override render(): TemplateResult {
    return html`
      <div class="page">
        <header>
          <button class="back" aria-label="Back" @click=${this.close}>${backIcon}</button>
          <h1>Settings</h1>
        </header>
        <div class="body">
          <reading-bee-instructor-gate>${this.settingsView()}</reading-bee-instructor-gate>
        </div>
        <reading-bee-modal class="color-modal" @ModelClosing=${this.closeColorPicker}>
          <h2 slot="title" class="picker-title">Choose a color</h2>
          <div slot="body">${this.colorPickerBody()}</div>
        </reading-bee-modal>
        <reading-bee-modal class="delete-modal" @ModelClosing=${this.closeDelete}>
          ${
            this.pendingDeleteName
              ? html`
                  <h2 slot="title">
                    ${
                      this.pendingDeleteId && appStore.isIncomingShare(this.pendingDeleteId)
                        ? `Stop receiving ${this.pendingDeleteName}?`
                        : `Delete ${this.pendingDeleteName}?`
                    }
                  </h2>
                `
              : ""
          }
          <div slot="body">${this.deleteBody()}</div>
        </reading-bee-modal>
        <reading-bee-modal class="level-modal" @ModelClosing=${this.closeLevel}>
          <h2 slot="title">Set level</h2>
          <div slot="body">${this.levelBody()}</div>
        </reading-bee-modal>
        <reading-bee-modal class="lock-modal" @ModelClosing=${this.closeLockModal}>
          ${
            this.lockStep === "explain"
              ? html`
                  <h2 slot="title">Unlocked</h2>
                `
              : ""
          }
          <div slot="body">${this.lockModalBody()}</div>
        </reading-bee-modal>
        <reading-bee-modal class="version-modal">
          <h2 slot="title">App version</h2>
          <div slot="body">${this.versionBody()}</div>
        </reading-bee-modal>
      </div>
    `;
  }

  private close = (): void => {
    if (this.updatingPasscode) {
      this.updatingPasscode = false;
      this.passcodeStep = "current";
      this.nextPasscode = "";
      return;
    }
    navigate("reading");
  };

  private settingsView(): TemplateResult {
    if (this.updatingPasscode) {
      return html`
        <reading-bee-passcode
          title=${this.passcodeTitle()}
          hint="4-digit instructor passcode"
          ?recoverable=${this.passcodeStep === "current"}
          @complete=${this.onUpdatePasscode}></reading-bee-passcode>
      `;
    }
    if (this.wipeStep === "confirm") {
      return html`
        <div class="confirm">
          <h2>Delete all app data?</h2>
          <p>This cannot be undone. Every profile, result, and setting will be permanently deleted.</p>
          <div class="confirm-row">
            <button class="ghost-btn" @click=${() => (this.wipeStep = "none")}>Cancel</button>
            <button class="danger-btn" @click=${this.confirmWipe}>Yes, delete everything</button>
          </div>
        </div>
      `;
    }
    if (this.wipeStep === "pin") {
      return html`
        <div class="confirm">
          <h2>Re-enter passcode</h2>
          <p>Enter the instructor passcode to permanently delete all data.</p>
          <reading-bee-passcode
            title="Confirm delete"
            hint="4-digit passcode"
            recoverable
            @complete=${this.onWipe}></reading-bee-passcode>
          <button class="ghost-btn" @click=${() => (this.wipeStep = "none")}>Cancel</button>
        </div>
      `;
    }
    return html`
      ${this.accountSection()}
      <div class="button-bar top-actions">
        <button class="ghost-btn instructions" @click=${() => navigate("instructions")}>
          ${instructionsIcon} Teacher Instructions
        </button>
        <button class="ghost-btn" @click=${this.shareApp}>${shareIcon} Share</button>
        <button class="ghost-btn lock-toggle" @click=${this.openLockModal}>
          ${
            appStore.isLocked()
              ? html`
                  ${lockIcon} Locked
                `
              : html`
                  ${unlockIcon} Unlocked
                `
          }
        </button>
      </div>
      <h2>Profiles</h2>
      <div class="stack">
        ${appStore.state.profiles.map((profile) => this.profileCard(profile))}
        <button class="skeleton" @click=${() => navigate("add-profile")}>
          <span class="ghost-swatch"></span>
          Add Profile
        </button>
      </div>
      <h2>App data</h2>
      <div class="button-bar data-actions">
        ${
          appStore.hasPasscode()
            ? html`
                <button class="ghost-btn" @click=${() => (this.updatingPasscode = true)}>
                  ${lockIcon} Update passcode
                </button>
              `
            : ""
        }
        <button class="ghost-btn" @click=${this.download}>${downloadIcon} Download</button>
        <button class="ghost-btn" @click=${() => navigate("upload")}>${uploadIcon} Upload</button>
        <button class="ghost-btn" @click=${this.openVersion}>${versionIcon} App version</button>
      </div>
      <div class="danger">
        <h2>Danger zone</h2>
        <p>
          Permanently delete all Reading Bee data on this device.
          ${this.authUser ? " Unsynced updates will also be removed." : ""}
        </p>
        <button class="danger-btn" @click=${() => (this.wipeStep = "confirm")}>${trashIcon} Delete all app data</button>
      </div>
    `;
  }

  private profileCard(profile: Profile): TemplateResult {
    return html`
      <div class="profile-card" @click=${() => this.openTeacher(profile.id)}>
        <div class="profile-top">
          <button
            class="swatch"
            aria-label="Change color"
            style=${avatarStyle(profile.primaryColor, profile.secondaryColor)}
            @click=${(event: Event) => this.openColorPicker(profile.id, event)}>
            ${profileInitial(profile.name)}
          </button>
          <button
            class="identity"
            aria-label="Teacher view for ${profile.name}"
            @click=${(event: Event) => this.openTeacher(profile.id, event)}>
            <strong>${profile.name}${appStore.isIncomingShare(profile.id) ? " (shared)" : ""}</strong>
            <small>${this.profileProgress(profile)}</small>
          </button>
          <div class="level-wrap">
            <button
              class="level"
              aria-label="Set exact level"
              @click=${(event: Event) => this.openLevel(profile.id, event)}>
              Lv ${profile.level}
            </button>
          </div>
          ${
            appStore.isIncomingShare(profile.id)
              ? ""
              : html`
                  <button
                    class="muted-btn icon-share"
                    aria-label="Share profile"
                    @click=${(event: Event) => this.openShare(profile.id, event)}>
                    ${shareIcon}
                  </button>
                `
          }
          <button
            class="muted-btn icon-delete"
            aria-label=${appStore.isIncomingShare(profile.id) ? "Stop receiving profile" : "Remove profile"}
            @click=${(event: Event) => this.openDelete(profile.id, event)}>
            ${trashIcon}
          </button>
        </div>
      </div>
    `;
  }

  private profileProgress(profile: Profile): TemplateResult {
    return html`
      ${(["reading", "math", "spelling"] as const).map((subject) => {
        const summary = subjectSummary(profile, subject);
        return summary.participated
          ? html`
              <span>${summary.label} · Level ${summary.level}</span>
            `
          : "";
      })}
    `;
  }

  private openTeacher(profileId: string, event?: Event): void {
    event?.stopPropagation();
    navigate("edit-profile", profileId);
  }

  private accountSection(): TemplateResult {
    const configured = isAuthConfigured();
    const label = this.authUser?.name || this.authUser?.email || "Instructor";
    const needsReauthentication = this.syncStatus.needsSignIn;
    const pendingLabel = `${this.syncStatus.pending} pending update${this.syncStatus.pending === 1 ? "" : "s"}`;
    const pendingMessage = needsReauthentication
      ? `${pendingLabel}. They are saved on this device and will sync after you sign in again.`
      : typeof navigator !== "undefined" && navigator.onLine === false
        ? `${pendingLabel}. Updates will sync when network connectivity is restored.`
        : `${pendingLabel} waiting to sync.`;
    return html`
      <h2 class="account-heading">Account</h2>
      <div class="account">
        ${
          this.authUser
            ? html`
                <div class="account-row">
                  <p>
                    Signed in as
                    <b>${label}</b>
                  </p>
                  <button class="ghost-btn sign-out" ?disabled=${this.authBusy} @click=${this.signOut}>Sign out</button>
                </div>
                ${
                  this.syncStatus.pending > 0
                    ? html`
                        <p>${pendingMessage}</p>
                      `
                    : ""
                }
                ${
                  this.syncStatus.error
                    ? html`
                        <p class=${needsReauthentication ? "" : "sync-error"}>${this.syncStatus.error}</p>
                      `
                    : ""
                }
                ${
                  needsReauthentication && configured
                    ? html`
                        <div class="actions">
                          <button class="primary-btn sign-in" ?disabled=${this.authBusy} @click=${this.signIn}>
                            Sign in again
                          </button>
                        </div>
                      `
                    : ""
                }
              `
            : html`
                <p>
                  Sign in to sync instructor data across browsers. Students do not use this, and reading still works
                  offline.
                </p>
                ${
                  configured
                    ? ""
                    : html`
                        <p>
                          Sign-in is not set up on this build. Add AUTH0_DOMAIN and AUTH0_CLIENT_ID, then restart the
                          app.
                        </p>
                      `
                }
                <div class="actions">
                  <button class="primary-btn sign-in" ?disabled=${this.authBusy || !configured} @click=${this.signIn}>
                    ${needsReauthentication ? "Reauthenticate" : "Sign in"}
                  </button>
                </div>
              `
        }
      </div>
    `;
  }

  private signIn = async (): Promise<void> => {
    this.authBusy = true;
    try {
      await loginInstructor();
    } catch {
      this.authBusy = false;
      dispatch(this, WarningEvent("Could not start sign-in"));
    }
  };

  private signOut = async (): Promise<void> => {
    this.authBusy = true;
    try {
      await flushSqlSync();
      await clearSqlLocalData();
      appStore.wipeAll();
      await logoutInstructor();
    } catch {
      this.authBusy = false;
      dispatch(this, WarningEvent(getSqlSyncStatus().error || "Could not sign out"));
    }
  };

  private colorPickerBody(): TemplateResult {
    const profile = appStore.state.profiles.find((item) => item.id === this.colorPickerProfileId);
    if (!profile) {
      return html``;
    }
    return html`
      <div class="pairs">
        ${COLOR_PAIRS.map(
          (pair, index) => html`
            <button
              class="pair"
              ?selected=${profile.colorPairIndex === index}
              style="background: linear-gradient(135deg, ${pair.primary} 0 50%, ${pair.secondary} 50% 100%);"
              aria-label="Color pair ${index + 1}"
              @click=${() => this.pickColor(profile.id, index)}></button>
          `,
        )}
      </div>
    `;
  }

  private levelBody(): TemplateResult {
    const profile = appStore.state.profiles.find((item) => item.id === this.levelProfileId);
    if (!profile) {
      return html``;
    }
    const sample = this.levelSubject === "reading" ? sampleTextAtLevel(this.draftLevel) : null;
    const mathSample =
      this.levelSubject === "math" ? questionForLevel(this.draftLevel, profile.subjectProgress?.math) : null;
    const spellingSample =
      this.levelSubject === "spelling" ? (SPELLING_CURRICULUM[this.draftLevel - 1]?.words[0] ?? "") : "";
    const picture = sample ? pictureFor(sample) : undefined;
    const sampleText = sample?.text ?? mathSample?.text ?? spellingSample;
    const sampleKind = sample?.kind ?? "word";
    return html`
      <div class="level-form">
        <p>Choose a level from ${MIN_LEVEL} to ${MAX_LEVEL} for ${profile.name}.</p>
        <div class="level-subjects" role="group" aria-label="Subject">
          ${(["reading", "math", "spelling"] as const).map(
            (subject) => html`
              <button
                class="muted-btn"
                ?data-active=${this.levelSubject === subject}
                @click=${() => this.selectLevelSubject(profile, subject)}>
                ${subject === "reading" ? "Reading" : subject === "math" ? "Math" : "Spelling"}
              </button>
            `,
          )}
        </div>
        <div class="level-controls">
          <input
            type="range"
            min=${MIN_LEVEL}
            max=${MAX_LEVEL}
            .value=${String(this.draftLevel)}
            aria-label="Level slider"
            @input=${this.onDraftLevel} />
          <input
            type="number"
            min=${MIN_LEVEL}
            max=${MAX_LEVEL}
            .value=${String(this.draftLevel)}
            aria-label="Level number"
            @input=${this.onDraftLevel} />
        </div>
        <p class="sample-label">Sample at level ${this.draftLevel}</p>
        <div
          class="sample ${picture ? "has-picture" : ""} ${(sampleText.length ?? 0) > 100 ? "is-long" : ""}"
          data-kind=${sampleKind}>
          <span>${sampleText}</span>
          ${
            picture
              ? html`
                  <img class="picture" src=${picture} alt="" aria-hidden="true" />
                `
              : ""
          }
        </div>
        <div class="confirm-row">
          <button class="ghost-btn" @click=${() => this.levelModal.close()}>Cancel</button>
          <button class="primary-btn" @click=${this.saveLevel}>Save level</button>
        </div>
      </div>
    `;
  }

  private deleteBody(): TemplateResult {
    if (!this.pendingDeleteName) {
      return html``;
    }
    const incoming = Boolean(this.pendingDeleteId && appStore.isIncomingShare(this.pendingDeleteId));
    return html`
      <div class="confirm">
        <p>
          ${
            incoming
              ? "This student is no longer being shared with you"
              : "This will permanently remove this profile and their reading history. This cannot be undone."
          }
        </p>
        <div class="confirm-row">
          <button class="ghost-btn" @click=${() => this.deleteModal.close()}>Cancel</button>
          <button class="danger-btn" @click=${this.confirmDelete}>
            ${incoming ? "Stop receiving" : "Delete profile"}
          </button>
        </div>
      </div>
    `;
  }

  private openColorPicker(profileId: string, event?: Event): void {
    event?.stopPropagation();
    this.colorPickerProfileId = profileId;
    void this.colorModal.open();
  }

  private closeColorPicker = (): void => {
    this.colorPickerProfileId = null;
  };

  private openLevel(profileId: string, event?: Event): void {
    event?.stopPropagation();
    const profile = appStore.state.profiles.find((item) => item.id === profileId);
    if (!profile) return;
    this.levelProfileId = profile.id;
    this.levelSubject = "reading";
    this.draftLevel = profile.subjectProgress?.reading.level ?? profile.level;
    void this.levelModal.open();
  }

  private selectLevelSubject(profile: Profile, subject: Subject): void {
    this.levelSubject = subject;
    this.draftLevel = profile.subjectProgress?.[subject]?.level ?? (subject === "reading" ? profile.level : MIN_LEVEL);
  }

  private closeLevel = (): void => {
    this.levelProfileId = null;
  };

  private onDraftLevel = (event: Event): void => {
    const raw = Number((event.target as HTMLInputElement).value);
    if (!Number.isFinite(raw)) return;
    this.draftLevel = Math.min(MAX_LEVEL, Math.max(MIN_LEVEL, Math.round(raw)));
  };

  private saveLevel = async (): Promise<void> => {
    const id = this.levelProfileId;
    if (!id) return;
    appStore.setSubjectLevel(id, this.levelSubject, this.draftLevel);
    await this.levelModal.close();
    dispatch(this, SuccessEvent("Level updated"));
  };

  private openDelete(profileId: string, event?: Event): void {
    event?.stopPropagation();
    const profile = appStore.state.profiles.find((item) => item.id === profileId);
    if (!profile) return;
    this.pendingDeleteId = profile.id;
    this.pendingDeleteName = profile.name;
    void this.deleteModal.open();
  }

  private closeDelete = (): void => {
    this.pendingDeleteId = null;
  };

  private confirmDelete = async (): Promise<void> => {
    const id = this.pendingDeleteId;
    if (!id) return;
    await this.deleteModal.close();
    await this.removeProfile(id);
  };

  private pickColor(profileId: string, index: number): void {
    appStore.recolorProfile(profileId, index);
    void this.colorModal.close();
  }

  private passcodeTitle(): string {
    if (this.passcodeStep === "current") return "Current passcode";
    if (this.passcodeStep === "next") return "New passcode";
    return "Confirm new passcode";
  }

  private lockPadTitle(): string {
    if (this.lockStep === "create") return "Create passcode";
    if (this.lockStep === "confirm") return "Confirm passcode";
    return "Enter passcode";
  }

  private lockPadHint(): string {
    if (this.lockStep === "disable") return "Enter the passcode to unlock the app";
    return "4-digit instructor passcode";
  }

  private lockModalBody(): TemplateResult {
    if (this.lockStep === "explain") {
      return html`
        <div class="lock-explain">
          <p>The app is currently unlocked. Anyone using this device can open settings and switch student profiles.</p>
          <p>Create a 4-digit passcode to keep the student from accessing settings and other student profiles.</p>
          <button class="primary-btn" @click=${this.startCreateLock}>Create passcode</button>
        </div>
      `;
    }
    return html`
      <div class="lock-pad">
        <reading-bee-passcode
          title=${this.lockPadTitle()}
          hint=${this.lockPadHint()}
          ?recoverable=${this.lockStep === "disable"}
          @complete=${this.onLockPasscode}></reading-bee-passcode>
      </div>
    `;
  }

  private openLockModal = (): void => {
    if (appStore.isLocked()) {
      this.lockStep = "disable";
      this.pendingLockPasscode = "";
      void this.lockModal.open();
      return;
    }
    if (appStore.hasPasscode()) {
      appStore.lockInstructor();
      dispatch(this, SuccessEvent("Locked"));
      return;
    }
    this.lockStep = "explain";
    this.pendingLockPasscode = "";
    void this.lockModal.open();
  };

  private closeLockModal = (): void => {
    this.pendingLockPasscode = "";
  };

  private startCreateLock = (): void => {
    this.lockStep = "create";
    this.pendingLockPasscode = "";
  };

  private onLockPasscode = (event: Event): void => {
    const value = (event as CustomEvent<{ value: string }>).detail.value;
    const pad = this.padFrom(event);
    if (this.lockStep === "create") {
      this.pendingLockPasscode = value;
      this.lockStep = "confirm";
      pad.reset();
      return;
    }
    if (this.lockStep === "confirm") {
      if (value !== this.pendingLockPasscode) {
        this.pendingLockPasscode = "";
        this.lockStep = "create";
        pad.shake();
        return;
      }
      appStore.setPasscode(value);
      appStore.lockInstructor();
      appStore.unlockInstructorSession();
      this.pendingLockPasscode = "";
      void this.lockModal.close();
      dispatch(this, SuccessEvent("Passcode created"));
      return;
    }
    if (!appStore.verifyPasscode(value)) {
      pad.shake();
      return;
    }
    appStore.unlockInstructor();
    void this.lockModal.close();
    dispatch(this, SuccessEvent("Unlocked"));
  };

  private confirmWipe = (): void => {
    if (appStore.hasPasscode()) {
      this.wipeStep = "pin";
      return;
    }
    void this.performWipe();
  };

  private padFrom(event: Event): ReadingBeePasscode {
    return event.currentTarget as ReadingBeePasscode;
  }

  private onUpdatePasscode = (event: Event): void => {
    const value = (event as CustomEvent<{ value: string }>).detail.value;
    const pad = this.padFrom(event);
    if (this.passcodeStep === "current") {
      if (!appStore.verifyPasscode(value)) {
        pad.shake();
        return;
      }
      this.passcodeStep = "next";
      pad.reset();
      return;
    }
    if (this.passcodeStep === "next") {
      this.nextPasscode = value;
      this.passcodeStep = "confirm";
      pad.reset();
      return;
    }
    if (value !== this.nextPasscode) {
      this.nextPasscode = "";
      this.passcodeStep = "next";
      pad.shake();
      return;
    }
    appStore.setPasscode(value);
    this.updatingPasscode = false;
    this.passcodeStep = "current";
    this.nextPasscode = "";
    dispatch(this, SuccessEvent("Passcode updated"));
  };

  private onWipe = (event: Event): void => {
    const value = (event as CustomEvent<{ value: string }>).detail.value;
    if (!appStore.verifyPasscode(value)) {
      this.padFrom(event).shake();
      return;
    }
    void this.performWipe();
  };

  private performWipe = async (): Promise<void> => {
    try {
      await clearSqlLocalData();
      appStore.wipeAll();
      if (getAuthUser()) await logoutInstructor();
      navigate("reading");
    } catch (error) {
      dispatch(this, WarningEvent(error instanceof Error ? error.message : "Could not clear local Reading Bee data"));
    }
  };

  private removeProfile = async (id: string): Promise<void> => {
    const share = appStore.incomingShareFor(id);
    const incoming = Boolean(share);
    if (share?.shareId) {
      try {
        await readingBeeApi.leaveShare(share.shareId);
      } catch (error) {
        dispatch(this, WarningEvent(error instanceof Error ? error.message : "Could not stop receiving this student"));
      }
    }
    appStore.removeProfile(id);
    if (incoming) await removeSqlProfileData(id);
    this.pendingDeleteId = null;
    dispatch(this, SuccessEvent(incoming ? "Stopped receiving this profile" : "Profile removed"));
    if (appStore.state.profiles.length === 0) navigate("reading");
  };

  private openShare(profileId: string, event?: Event): void {
    event?.stopPropagation();
    navigate("share-profile", profileId);
  }

  private shareApp = async (): Promise<void> => {
    try {
      const result = await shareAppLink();
      if (result === "cancelled") return;
      if (result === "copied") {
        dispatch(this, SuccessEvent("Link copied"));
      }
    } catch {
      dispatch(this, WarningEvent("Could not share Reading Bee"));
    }
  };

  private versionBody(): TemplateResult {
    const device = rememberDeviceVersion();
    return html`
      <dl class="version-facts">
        <div>
          <dt>Version</dt>
          <dd>${APP_SEMVER}</dd>
        </div>
        <div>
          <dt>Released</dt>
          <dd>${formatAppTimestamp(APP_RELEASED_AT)}</dd>
        </div>
        <div>
          <dt>This device updated</dt>
          <dd>${formatAppTimestamp(device.updatedAt)}</dd>
        </div>
      </dl>
    `;
  }

  private openVersion = (): void => {
    rememberDeviceVersion();
    void this.versionModal.open();
  };

  private download = async (): Promise<void> => {
    const backup = await createBackup(appStore, getSqlOutbox());
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "reading-bee-data.json";
    link.click();
    URL.revokeObjectURL(url);
    dispatch(this, SuccessEvent("Backup downloaded"));
  };
}
